import { formatLocalDate } from "../../../shared/local-date";
import {
  METRIC_DENOMINATOR_LABELS,
  METRIC_SOURCE_LABELS,
  metricReasonText,
  type CompactMetricEnvelope,
  type MetricChip,
  type MetricName,
} from "../../../shared/metrics";

/*
 * Pure display helpers for the RD Snapshot headline strip. Everything here is
 * presentation: the server's envelope already carries the chip, the target,
 * the watch band, the fleet figures, and the sentence. Nothing in this module
 * grades a value. Relative imports only: the `@/` alias does not resolve under
 * `tsx --test`, and the component tests import this module directly.
 */

export interface HeadlineMetricConfig {
  name: MetricName;
  title: string;
  /** Word shown after the value; derived from unit + denominator when absent. */
  unitWord?: string;
}

/** The six headline tiles in their 2x3 order (left to right, top to bottom). */
export const HEADLINE_METRICS: readonly HeadlineMetricConfig[] = [
  { name: "billableHours", title: "Billable hours vs minimum" },
  { name: "billableUtilization", title: "Billable utilization" },
  { name: "forwardLoad", title: "Forward load", unitWord: "hrs booked" },
  { name: "retention", title: "Retention", unitWord: "retained" },
  { name: "cancellationRate", title: "Cancellation rate" },
  { name: "visitCadence", title: "Visit cadence" },
];

export const MISSING_VALUE = "—";

function decimals(metric: Pick<CompactMetricEnvelope, "unit" | "per">): number {
  if (metric.unit === "percent" || metric.unit === "referrals") return 0;

  if (metric.unit === "visits" && metric.per !== "absolute") return 2;

  return 1;
}

function formatNumber(
  value: number,
  metric: Pick<CompactMetricEnvelope, "unit" | "per">
): string {
  const digits = decimals(metric);
  const text =
    Number.isInteger(value) && digits === 0 ? String(value) : value.toFixed(digits);

  return metric.unit === "percent" ? `${text}%` : text;
}

/** Value text for the tile. `null` is always a dash, never a zero. */
export function formatValue(
  metric: Pick<CompactMetricEnvelope, "value" | "unit" | "per">
): string {
  if (metric.value === null) return MISSING_VALUE;

  return formatNumber(metric.value, metric);
}

/** Word after the value: "hrs/wk", "visits/wk", "hrs", or a per-metric override. */
export function unitWord(
  metric: Pick<CompactMetricEnvelope, "unit" | "per">,
  config?: Pick<HeadlineMetricConfig, "unitWord">
): string {
  if (config?.unitWord) return config.unitWord;

  if (metric.unit === "percent") return "";

  const base = metric.unit === "hours" ? "hrs" : metric.unit;

  return `${base}${METRIC_DENOMINATOR_LABELS[metric.per].short}`;
}

export interface ChipTokens {
  /** Classes for the chip pill. */
  pill: string;
  /** Classes for the goal-bar fill. */
  fill: string;
  /** True for the neutral (ungraded) states. */
  neutral: boolean;
}

// Meets is mint and Above is the darker green, per the approved frames. The
// app's rating spectrum in components/badge.tsx uses "meets" for yellow, so
// the metric chip keeps its own vocabulary here instead of composing Badge.
// Under the always theme the chip is a highlighter behind the word and the
// fill is olive, red only below (D4, D5; ADR-0004's 2026-10-22 amendment).
// The pill reads the theme-only highlighter values with its classic colors as
// fallbacks, like components/badge.tsx; the fill's brand classes apply only
// under the theme.
const CHIP_TOKENS: Record<MetricChip, ChipTokens> = {
  Meets: {
    pill: "bg-(--highlight-fill,var(--color-success-soft)) text-(color:--highlight-ink,var(--color-success)) bg-(image:--highlight-yes)",
    fill: "bg-success/60 brand:bg-metric-meets",
    neutral: false,
  },
  Above: {
    pill: "bg-(--highlight-fill,var(--color-forest-500)) text-(color:--highlight-ink,var(--color-forest-100)) bg-(image:--highlight-yes)",
    fill: "bg-success brand:bg-metric-meets",
    neutral: false,
  },
  Watch: {
    pill: "bg-(--highlight-fill,var(--color-sunlight-100)) text-(color:--highlight-ink,var(--color-sunlight-500)) bg-(image:--highlight-meh)",
    fill: "bg-sunlight-500/70 brand:bg-metric-watch",
    neutral: false,
  },
  Below: {
    pill: "bg-(--highlight-fill,var(--color-danger-soft)) text-(color:--highlight-ink-no,var(--destructive)) bg-(image:--highlight-no)",
    fill: "bg-error/70 brand:bg-metric-below",
    neutral: false,
  },
  "Too early": {
    pill: "border border-(color:--highlight-border,var(--border)) bg-transparent text-muted-foreground",
    fill: "bg-sand-300",
    neutral: true,
  },
  "No target": {
    pill: "border border-(color:--highlight-border,var(--border)) bg-transparent text-muted-foreground",
    fill: "bg-sand-300",
    neutral: true,
  },
};

export function chipTokens(chip: MetricChip): ChipTokens {
  return CHIP_TOKENS[chip];
}

export interface BarZone {
  startPct: number;
  endPct: number;
}

export interface BarGeometry {
  axisMin: number;
  axisMax: number;
  /** Mint good zone, or null when the envelope names no target. */
  good: BarZone | null;
  /** Amber watch band from the envelope, or null. */
  watch: BarZone | null;
  /** Threshold ticks at the target's own bounds (never at the axis edges). */
  ticksPct: number[];
  /** Fill width; 0 when the value is null. */
  fillPct: number;
  /** Dotted fleet-median marker, or null when the envelope carries none. */
  fleetPct: number | null;
}

function goodBounds(
  band: CompactMetricEnvelope["good"]
): { min: number | null; max: number | null; ticks: number[] } | null {
  if (!band) return null;

  return {
    ...band,
    ticks: [band.min, band.max].filter((bound): bound is number => bound !== null),
  };
}

type GoodBounds = NonNullable<ReturnType<typeof goodBounds>>;

interface Axis {
  axisMin: number;
  axisMax: number;
}

function finiteCandidates(
  metric: Pick<CompactMetricEnvelope, "value" | "watch" | "fleet">,
  good: GoodBounds | null
): number[] {
  return [
    metric.value,
    good?.min ?? null,
    good?.max ?? null,
    metric.watch?.min ?? null,
    metric.watch?.max ?? null,
    metric.fleet?.median ?? null,
    metric.fleet?.p75 ?? null,
  ].filter((candidate): candidate is number => candidate !== null);
}

/** Every metric here is non-negative; percent axes always reach 100. */
function axisFor(unit: CompactMetricEnvelope["unit"], candidates: number[]): Axis {
  const largest = candidates.length > 0 ? Math.max(...candidates) : 0;

  if (unit === "percent") return { axisMin: 0, axisMax: Math.max(100, largest) };

  return { axisMin: 0, axisMax: largest > 0 ? largest * 1.15 : 1 };
}

function positionOn(axis: Axis): (n: number) => number {
  const span = axis.axisMax - axis.axisMin;

  return (n: number): number =>
    Math.min(100, Math.max(0, ((n - axis.axisMin) / span) * 100));
}

function zoneOn(axis: Axis, min: number | null, max: number | null): BarZone {
  const pct = positionOn(axis);

  return { startPct: pct(min ?? axis.axisMin), endPct: pct(max ?? axis.axisMax) };
}

function fleetPosition(
  fleet: CompactMetricEnvelope["fleet"],
  pct: (n: number) => number
): number | null {
  if (!fleet || fleet.median === null) return null;

  return pct(fleet.median);
}

/** Geometry for the zoned goal bar, as percentages of the bar width. */
export function barGeometry(
  metric: Pick<CompactMetricEnvelope, "value" | "unit" | "good" | "watch" | "fleet">
): BarGeometry {
  const good = goodBounds(metric.good);
  const axis = axisFor(metric.unit, finiteCandidates(metric, good));
  const pct = positionOn(axis);

  return {
    ...axis,
    good: good ? zoneOn(axis, good.min, good.max) : null,
    watch: metric.watch ? zoneOn(axis, metric.watch.min, metric.watch.max) : null,
    ticksPct: good ? good.ticks.map(pct) : [],
    fillPct: metric.value === null ? 0 : pct(metric.value),
    fleetPct: fleetPosition(metric.fleet, pct),
  };
}

/** The tile's one-line read: the server sentence, else its reason, else a neutral line. */
export function tileSentence(
  metric: Pick<CompactMetricEnvelope, "sentence" | "reason">
): string {
  if (metric.sentence !== null) return metric.sentence;

  return metricReasonText(metric.reason) ?? "No read yet.";
}

export function targetRowText(metric: Pick<CompactMetricEnvelope, "target">): string {
  return metric.target?.label ?? "No target";
}

/** Text for the All RDs comparison row (the envelope's fleet figures). */
export function fleetRowText(
  metric: Pick<CompactMetricEnvelope, "fleet" | "unit" | "per">
): string {
  const fleet = metric.fleet;
  if (!fleet || fleet.median === null) return MISSING_VALUE;

  const median = `median ${formatNumber(fleet.median, metric)}`;

  return fleet.p75 === null
    ? median
    : `${median} · p75 ${formatNumber(fleet.p75, metric)}`;
}

/** Human label for the envelope's source. */
export function sourceLabel(source: CompactMetricEnvelope["source"]): string {
  return METRIC_SOURCE_LABELS[source];
}

export function formatDataThrough(dataThrough: string): string {
  return formatLocalDate(dataThrough.slice(0, 10));
}

export interface DefinitionCopy {
  definition: string;
  zones: string;
  source: string;
  dataThrough: string;
  window: string;
}

/** Copy for the definition popover, composed only from envelope fields. */
export function definitionCopy(
  metric: Pick<
    CompactMetricEnvelope,
    | "definition"
    | "target"
    | "watch"
    | "fleet"
    | "unit"
    | "per"
    | "source"
    | "dataThrough"
    | "window"
  >
): DefinitionCopy {
  const parts: string[] = [];
  if (metric.target) parts.push(`Target ${metric.target.label} (green band).`);
  else parts.push("No target set, so no green band is drawn.");

  if (metric.watch) {
    parts.push(
      `Amber band ${formatNumber(metric.watch.min ?? 0, metric)}–${formatNumber(metric.watch.max ?? 0, metric)} is worth a look.`
    );
  }

  if (metric.fleet && metric.fleet.median !== null) {
    parts.push(
      `The dotted mark is the median for ${metric.fleet.label} (${metric.fleet.rdCount} RDs).`
    );
  }

  return {
    definition: metric.definition,
    zones: parts.join(" "),
    source: sourceLabel(metric.source),
    dataThrough: formatDataThrough(metric.dataThrough),
    window: metric.window.label,
  };
}
